/**
 * Day Configuration Component
 * 
 * Configures a single day of the week for bulk event creation.
 * Allows enabling/disabling the day and setting event details.
 * 
 * Requirements: 3.1-3.12
 */

import React from 'react';
import { FormField } from '../../common/FormField';
import type { DayConfig } from '../../../utils/bulkEventsValidation';
import type { EventType, Location } from '../../../api/rest-client';

export interface DayConfigurationProps {
  dayOfWeek: number; // 0=Sunday, 1=Monday, ..., 6=Saturday
  config: DayConfig;
  eventTypes: EventType[];
  locations: Location[];
  errors?: Record<string, string>;
  onChange: (config: DayConfig) => void;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Get default times based on day of week
 * Weekdays (Mon-Fri): 18:30-20:00
 * Weekends (Sat-Sun): 14:00-16:00
 */
function getDefaultTimes(dayOfWeek: number): { startTime: string; endTime: string } {
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  return {
    startTime: isWeekend ? '14:00' : '18:30',
    endTime: isWeekend ? '16:00' : '20:00',
  };
}

/**
 * Day Configuration Component
 * Requirements: 3.1-3.12
 */
export function DayConfiguration({
  dayOfWeek,
  config,
  eventTypes,
  locations,
  errors = {},
  onChange,
}: DayConfigurationProps) {
  const dayName = DAY_NAMES[dayOfWeek];
  
  const handleEnabledChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = e.target.checked;
    const defaults = getDefaultTimes(dayOfWeek);
    
    onChange({
      ...config,
      enabled,
      // Set defaults when enabling
      eventName: enabled && !config.eventName ? `${dayName} Practice` : config.eventName,
      eventType: enabled && !config.eventType ? 'Practice' : config.eventType,
      startTime: enabled && !config.startTime ? defaults.startTime : config.startTime,
      endTime: enabled && !config.endTime ? defaults.endTime : config.endTime,
    });
  };
  
  const handleFieldChange = (field: keyof DayConfig) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    onChange({
      ...config,
      [field]: e.target.value,
    });
  };
  
  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      {/* Day Enable Checkbox */}
      <div className="flex items-center mb-4">
        <input
          type="checkbox"
          id={`day-${dayOfWeek}-enabled`}
          checked={config.enabled}
          onChange={handleEnabledChange}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
        />
        <label
          htmlFor={`day-${dayOfWeek}-enabled`}
          className="ml-2 block text-sm font-medium text-gray-900"
        >
          {dayName}
        </label>
      </div>
      
      {/* Configuration Fields - Only shown when enabled */}
      {config.enabled && (
        <div className="space-y-3 pl-6">
          <FormField
            label="Event Name"
            name={`day-${dayOfWeek}-eventName`}
            type="text"
            value={config.eventName}
            error={errors.eventName}
            touched={true}
            required
            placeholder={`${dayName} Practice`}
            onChange={handleFieldChange('eventName')}
          />
          
          <FormField
            label="Event Type"
            name={`day-${dayOfWeek}-eventType`}
            type="select"
            value={config.eventType}
            error={errors.eventType}
            touched={true}
            required
            options={[...eventTypes]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(et => ({
                value: et.name,
                label: et.name,
              }))}
            onChange={handleFieldChange('eventType')}
          />
          
          <FormField
            label="Location"
            name={`day-${dayOfWeek}-locationId`}
            type="select"
            value={config.locationId}
            error={errors.locationId}
            touched={true}
            required
            options={[...locations]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(loc => ({
                value: loc.location_id,
                label: loc.name,
              }))}
            onChange={handleFieldChange('locationId')}
          />
          
          <div className="grid grid-cols-2 gap-3">
            <FormField
              label="Start Time"
              name={`day-${dayOfWeek}-startTime`}
              type="text"
              value={config.startTime}
              error={errors.startTime}
              touched={true}
              required
              placeholder="18:30"
              onChange={handleFieldChange('startTime')}
              helpText="Format: HH:MM"
            />
            
            <FormField
              label="End Time"
              name={`day-${dayOfWeek}-endTime`}
              type="text"
              value={config.endTime}
              error={errors.endTime}
              touched={true}
              required
              placeholder="20:00"
              onChange={handleFieldChange('endTime')}
              helpText="Format: HH:MM"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default DayConfiguration;
